import { useState, useEffect, useRef } from "react";

// ── Logos ─────────────────────────────────────────────────────────────────
import LOGO_GREEN from './logo.png';
import LOGO_WHITE from './logo_white.png';

// ── Your Real Images ──────────────────────────────────────────────────────
import IMG_FABRICATION from './fabrication.jpg';   // Welding / fabrication
import IMG_CUSTOM      from './custom.jpg';         // Custom part / drilling
import IMG_STAMP       from './stamp.jpg';          // Stamping / power press
import IMG_DIEMAKING   from './diemaking.jpg';      // Tooling & die making
import IMG_MACHINE     from './machine.jpg';        // Workshop machine (about/hero)
import IMG_WORKER      from './worker.jpg';         // Worker (about section)
import IMG_TOOLING     from './tooling.jpg';        // Die rack (quality/about)

// ── Firebase (uncomment after: npm install firebase) ──────────────────────
 import { initializeApp } from 'firebase/app'
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore'

const firebaseConfig = {
  apiKey: "AIzaSyB1xXqBa4l7RkR4e7Z-lnFSzTKdjVBxkmc",
  authDomain: "cartesian-tools.firebaseapp.com",
  projectId: "cartesian-tools",
  storageBucket: "cartesian-tools.firebasestorage.app",
  messagingSenderId: "532920979654",
  appId: "1:532920979654:web:aa0b7a967d50777bc287c7"
};



const app = initializeApp(firebaseConfig)
const db  = getFirestore(app)

// ── Design Tokens ─────────────────────────────────────────────────────────
const T = {
  bg:        "#f5f3ee",
  bgWhite:   "#ffffff",
  bgDark:    "#111111",
  card:      "#ffffff",
  border:    "#e0dbd0",
  borderDk:  "#252525",
  green:     "#3a6b1f",
  greenDk:   "#1e4411",
  greenMd:   "#4a8228",
  greenLt:   "#5aa030",
  greenPale: "#eaf2e5",
  greenGlow: "#6ec240",
  cream:     "#faf8f4",
  text:      "#1a1a1a",
  textMd:    "#3d3a35",
  textLt:    "#7a7570",
  textFt:    "#b0aba4",
  shadow:    "rgba(0,0,0,0.07)",
  shadowMd:  "rgba(0,0,0,0.15)",
};

const CO = {
  phone1: "9667939821", phone2: "8264292156",
  email:  "cartesiantools@gmail.com",
  hours:  "Mon – Sat  9:00 AM – 6:30 PM",
  addr:   "Delhi NCR, India",
};

const NAV      = ["Home","About","Services","Quality","Contact"];
const PARTNERS = ["FLEXO PACK","jena automotive","amadhur-industries","shibata pneumatic","jai bhagwati ","durga enterprises","Rajhans Pressings","paradise wire"];
const STATS    = [{v:"200+",l:"Parts Daily"},{v:"5+",l:"Years Exp."},{v:"100+",l:"Clients"},{v:"±0.05mm",l:"Tolerance"}];

// ── Services — using your real images ────────────────────────────────────
const SERVICES = [
  {
    icon:"⚙️", name:"Sheet Metal Fabrication", short:"Welding, cutting & forming",
    desc:"Complete sheet metal fabrication — from raw coil to finished component. We handle welding, cutting, bending, and forming in mild steel, stainless steel, and aluminium.",
    feat:["Welding & Assembly","Deep Drawing","Bending & Forming","Custom Profiles","Shearing","Blanking & Piercing"],
    img: IMG_FABRICATION,
  },
  {
    icon:"🔩", name:"Custom Part Manufacturing", short:"Exact-spec parts, 1 to 10,000+",
    desc:"Send your drawing or sample — we manufacture to exact dimensions. CAD files, sketches, or physical samples all accepted. Batch sizes from 1 piece to 10,000+.",
    feat:["CAD-Based Production","Tight Tolerances","Prototyping","Bulk Orders","All Steel Grades","Surface Finishing"],
    img: IMG_CUSTOM,
  },
  {
    icon:"🏭", name:"Stamping & Pressing", short:"High-volume metal stamping",
    desc:"High-volume metal stamping for automotive brackets, enclosures, structural hardware. Our power presses from 10T to 100T deliver consistent, accurate results every cycle.",
    feat:["Progressive Stamping","Compound Dies","High-Volume Runs","Auto Components","Consistent Accuracy","Quick Setup"],
    img: IMG_STAMP,
  },
  {
    icon:"🛠️", name:"Tooling & Die Making", short:"In-house die design & fabrication",
    desc:"We design and build press tools and dies in-house — blanking dies, bending tools, and progressive dies built to last millions of strokes with regular maintenance support.",
    feat:["Blanking Dies","Bending Tools","Progressive Dies","Die Maintenance","Tool Design","Repair & Rework"],
    img: IMG_DIEMAKING,
  },
];

// ── Machines — replaced Knuckle Joint with Fabrication & Welding ──────────
const MACHINES = [
  {icon:"🔧", name:"C-Type Power Press",      cap:"10T – 100T",  use:"Blanking, piercing & forming", img: IMG_STAMP},
  {icon:"⚡", name:"H-Frame Power Press",     cap:"10T – 100T", use:"Heavy stamping & deep drawing", img: IMG_MACHINE},
  {icon:"🔥", name:"Full Fabrication & Welding", cap:"All Materials", use:"Complete welding, cutting & assembly", img: IMG_FABRICATION},
];

const QUALITY = [
  {code:"ISO 9001:2015",   lbl:"Quality Management",   desc:"Certified QMS — every part meets international standards for dimensional accuracy and surface finish."},
  {code:"100% Inspection", lbl:"In-House QC",          desc:"Every batch inspected using calibrated gauges before dispatch. No compromises."},
  {code:"CMM Verified",    lbl:"Dimensional Accuracy", desc:"Critical parts verified ensuring tolerances as tight as ±0.05mm consistently."},
  {code:"On-Time",         lbl:"Delivery Guarantee",   desc:"Lean workflow ensures parts reach you when promised, every time."},
];

// ── Hooks ─────────────────────────────────────────────────────────────────
function useVW(){
  const [w,setW]=useState(window.innerWidth);
  useEffect(()=>{const h=()=>setW(window.innerWidth);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);
  return w;
}

function useInView(threshold=0.08){
  const ref=useRef(null);
  const [v,setV]=useState(false);
  useEffect(()=>{
    const o=new IntersectionObserver(([e])=>{if(e.isIntersecting)setV(true)},{threshold});
    if(ref.current)o.observe(ref.current);
    return()=>o.disconnect();
  },[]);
  return [ref,v];
}

// ── Reveal wrapper ────────────────────────────────────────────────────────
function Reveal({children,delay=0,y=24}){
  const [ref,v]=useInView();
  return(
    <div ref={ref} style={{
      opacity:v?1:0,
      transform:v?"translateY(0)":`translateY(${y}px)`,
      transition:`opacity .7s cubic-bezier(.4,0,.2,1) ${delay}s, transform .7s cubic-bezier(.4,0,.2,1) ${delay}s`,
    }}>{children}</div>
  );
}

// ── Animated image — zooms in as you scroll to it ─────────────────────────
function AnimImg({src,alt,style={},delay=0}){
  const [ref,v]=useInView(0.1);
  return(
    <div ref={ref} style={{overflow:"hidden",borderRadius:style.borderRadius||10,...style,padding:0}}>
      <img
        src={src} alt={alt} loading="lazy"
        style={{
          width:"100%", height:"100%", objectFit:"cover", display:"block",
          transform: v ? "scale(1)" : "scale(1.08)",
          transition:`transform 1.1s cubic-bezier(.4,0,.2,1) ${delay}s, opacity .8s ease ${delay}s`,
          opacity: v ? 1 : 0,
        }}
      />
    </div>
  );
}

// ── Logo ──────────────────────────────────────────────────────────────────
function Logo({size=100, variant="green", center=false, animated=false}){
  const src = variant==="white" ? LOGO_WHITE : LOGO_GREEN;
  return(
    <img src={src} alt="Cartesian Tools" style={{
      width:`${size}px`, height:"auto", maxWidth:"none", minWidth:`${size}px`,
      display:"block", flexShrink:0,
      ...(center?{marginLeft:"auto",marginRight:"auto"}:{}),
      ...(animated?{animation:"logoFloat 3.8s ease-in-out infinite"}:{}),
    }}/>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────
const EB=(col=T.greenMd)=>({fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.32em",color:col,textTransform:"uppercase",display:"block",marginBottom:10,fontWeight:700});
const H1S=(sz="clamp(34px,5vw,60px)")=>({fontFamily:"'Playfair Display',serif",fontSize:sz,fontWeight:900,color:T.text,margin:"0 0 18px",lineHeight:1.12});
const H2S=(sz="clamp(24px,3vw,42px)")=>({fontFamily:"'Playfair Display',serif",fontSize:sz,fontWeight:800,color:T.text,margin:"0 0 18px",lineHeight:1.15});
const PARA={fontFamily:"'DM Sans',sans-serif",fontSize:15,color:T.textMd,lineHeight:1.88,margin:0};
const CARD={background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:"26px 22px",boxShadow:`0 2px 18px ${T.shadow}`};

function Btn({children,onClick,v="primary",full=false,sx={}}){
  const base={fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",padding:"13px 26px",borderRadius:8,cursor:"pointer",border:"none",transition:"all .22s",width:full?"100%":"auto",...sx};
  const vs={
    primary:{background:T.green,color:"#fff",boxShadow:`0 4px 18px ${T.green}40`},
    outline:{background:"transparent",border:`1.5px solid ${T.green}`,color:T.green},
    ghost:  {background:"transparent",border:`1.5px solid ${T.border}`,color:T.textMd},
    white:  {background:"#fff",color:T.green,boxShadow:"0 4px 20px rgba(0,0,0,0.2)"},
  };
  return(
    <button onClick={onClick} style={{...base,...vs[v]}}
      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.opacity="0.9";}}
      onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.opacity="1";}}
    >{children}</button>
  );
}

// ── Navbar ────────────────────────────────────────────────────────────────
function Navbar({page,setPage}){
  const [scrolled,setScrolled]=useState(false);
  const [open,setOpen]=useState(false);
  const vw=useVW(); const mob=vw<768;
  useEffect(()=>{const h=()=>setScrolled(window.scrollY>36);window.addEventListener("scroll",h);return()=>window.removeEventListener("scroll",h);},[]);
  useEffect(()=>setOpen(false),[page]);
  useEffect(()=>{document.body.style.overflow=open?"hidden":"";},[open]);

  return(
    <>
      <nav style={{
        position:"fixed",top:0,left:0,right:0,zIndex:500,
        height:mob?60:70, padding:mob?"0 16px":"0 40px",
        background:(scrolled||open)?"rgba(245,243,238,0.97)":"rgba(245,243,238,0.88)",
        backdropFilter:"blur(18px) saturate(1.5)",
        WebkitBackdropFilter:"blur(18px) saturate(1.5)",
        borderBottom:`1px solid ${(scrolled||open)?T.border:"transparent"}`,
        boxShadow:scrolled?`0 4px 28px ${T.shadow}`:"none",
        display:"flex",alignItems:"center",justifyContent:"space-between",
        transition:"all .3s ease",
      }}>
        {mob?(
          <>
            <button onClick={()=>setOpen(o=>!o)} style={{background:"none",border:"none",cursor:"pointer",padding:8,display:"flex",flexDirection:"column",gap:5,zIndex:2,flexShrink:0}}>
              {[0,1,2].map(i=>(
                <span key={i} style={{display:"block",width:22,height:2,background:T.text,borderRadius:2,transition:"all .3s",
                  transform:open?(i===0?"rotate(45deg) translate(5px,5px)":i===2?"rotate(-45deg) translate(5px,-5px)":"none"):"none",
                  opacity:open&&i===1?0:1}}/>
              ))}
            </button>
            <button onClick={()=>setPage("Home")} style={{background:"none",border:"none",cursor:"pointer",position:"absolute",left:"50%",transform:"translateX(-50%)",display:"flex",flexDirection:"column",alignItems:"center",gap:1}}>
              <Logo size={54} variant="green"/>
              <span style={{fontFamily:"'Playfair Display',serif",fontSize:13,fontWeight:900,color:T.text,letterSpacing:"0.05em",lineHeight:1}}>CARTESIAN</span>
            </button>
            <div style={{width:38,flexShrink:0}}/>
          </>
        ):(
          <>
            <button onClick={()=>setPage("Home")} style={{background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
              <Logo size={64} variant="green"/>
              <div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:21,fontWeight:900,color:T.text,letterSpacing:"0.04em",lineHeight:1.1}}>𝖢 Λ 𝖱 𝖳 Ξ 𝖲 𝖨 Λ 𝖭</div>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:8,letterSpacing:"0.26em",color:T.textLt,textTransform:"uppercase",fontWeight:600,marginTop:1}}> </div>
              </div>
            </button>
            <div style={{display:"flex",gap:2,alignItems:"center"}}>
              {NAV.map(n=>(
                <button key={n} onClick={()=>setPage(n)} style={{background:page===n?T.greenPale:"none",border:"none",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:600,letterSpacing:"0.07em",textTransform:"uppercase",color:page===n?T.green:T.textLt,padding:"8px 15px",borderRadius:7,transition:"all .2s"}}>
                  {n}
                </button>
              ))}
              <Btn onClick={()=>setPage("Contact")} v="primary" sx={{marginLeft:8,padding:"9px 20px",fontSize:11}}>Get Quote</Btn>
            </div>
          </>
        )}
      </nav>

      {/* Mobile drawer */}
      <div style={{position:"fixed",inset:0,zIndex:499,background:T.cream,transform:open?"translateX(0)":"translateX(100%)",transition:"transform .38s cubic-bezier(.4,0,.2,1)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"80px 32px 48px"}}>
        <Logo size={100} variant="green" center animated/>
        <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:900,color:T.text,letterSpacing:"0.06em",margin:"12px 0 32px",textAlign:"center"}}>CARTESIAN</div>
        {NAV.map((n,i)=>(
          <button key={n} onClick={()=>setPage(n)} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"'Playfair Display',serif",fontSize:30,fontWeight:700,color:page===n?T.green:T.text,padding:"11px 0",width:"100%",textAlign:"center",borderBottom:`1px solid ${T.border}`,transition:`color .2s, transform .35s ease ${i*.06}s`,transform:open?"translateX(0)":"translateX(40px)"}}>
            {n}
          </button>
        ))}
        <div style={{marginTop:32,display:"flex",flexDirection:"column",gap:10,alignItems:"center"}}>
          <a href={`tel:${CO.phone1}`} style={{fontFamily:"'DM Sans',sans-serif",fontSize:16,color:T.green,textDecoration:"none",fontWeight:700}}>📞 {CO.phone1}</a>
          <a href={`mailto:${CO.email}`} style={{fontFamily:"'DM Sans',sans-serif",fontSize:13,color:T.textLt,textDecoration:"none"}}>✉ {CO.email}</a>
        </div>
      </div>
    </>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────
function Footer({setPage}){
  const vw=useVW(); const mob=vw<600;
  return(
    <footer style={{background:T.bgDark,padding:mob?"48px 20px 28px":"64px 40px 32px"}}>
      <div style={{maxWidth:1100,margin:"0 auto"}}>
        <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"2fr 1fr 1fr",gap:mob?36:48,marginBottom:44,paddingBottom:40,borderBottom:`1px solid ${T.borderDk}`}}>
          <div>
            {/* BIG footer logo centered */}
            <div style={{display:"flex",flexDirection:"column",alignItems:mob?"center":"flex-start",marginBottom:20}}>
              <Logo size={mob?160:200} variant="white" center={mob}/>
              <div style={{marginTop:12,textAlign:mob?"center":"left"}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:"#fff",fontWeight:900,letterSpacing:"0.04em"}}>CARTESIAN</div>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:8,letterSpacing:"0.24em",color:"#555",textTransform:"uppercase",fontWeight:600,marginTop:2}}>Tools & Engineering</div>
              </div>
            </div>
            <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:13,color:"#777",lineHeight:1.78,maxWidth:300,textAlign:mob?"center":"left"}}>
              Precision sheet metal fabrication & custom part manufacturing in Delhi NCR. Trusted by 100+ companies for 5+ years.
            </p>
            <a href={`tel:${CO.phone1}`} style={{display:"inline-block",fontFamily:"'DM Sans',sans-serif",fontSize:14,color:T.greenGlow,textDecoration:"none",fontWeight:700,marginTop:16}}>📞 {CO.phone1}</a>
          </div>
          <div>
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:9,letterSpacing:"0.26em",color:T.greenGlow,textTransform:"uppercase",marginBottom:16,fontWeight:700}}>Pages</div>
            {NAV.map(n=>(<button key={n} onClick={()=>setPage(n)} style={{display:"block",background:"none",border:"none",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontSize:14,color:"#777",marginBottom:10,padding:0,textAlign:"left"}}>{n}</button>))}
          </div>
          <div>
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:9,letterSpacing:"0.26em",color:T.greenGlow,textTransform:"uppercase",marginBottom:16,fontWeight:700}}>Contact</div>
            {[{t:`📞 ${CO.phone1}`,h:`tel:${CO.phone1}`},{t:`📞 ${CO.phone2}`,h:`tel:${CO.phone2}`},{t:`✉ ${CO.email}`,h:`mailto:${CO.email}`}].map((c,i)=>(<a key={i} href={c.h} style={{display:"block",fontFamily:"'DM Sans',sans-serif",fontSize:13,color:"#777",textDecoration:"none",marginBottom:8}}>{c.t}</a>))}
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,color:"#555",marginTop:10,lineHeight:1.75}}>📍 {CO.addr}<br/>🕐 {CO.hours}</div>
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
          <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,color:"#3a3a3a"}}>© {new Date().getFullYear()} Cartesian Tools. All rights reserved.</span>
          <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,color:"#3a3a3a"}}>Sheet Metal Fabrication · Power Press Stamping · Delhi NCR</span>
        </div>
      </div>
    </footer>
  );
}

// ── HOME ──────────────────────────────────────────────────────────────────
function HomePage({setPage}){
  const [tick,setTick]=useState(0);
  const vw=useVW(); const mob=vw<600; const tab=vw<1024;
  useEffect(()=>{const t=setInterval(()=>setTick(p=>(p+1)%PARTNERS.length),2200);return()=>clearInterval(t);},[]);

  return(
    <div>
      {/* ── HERO */}
      <section style={{
        minHeight:"100vh",position:"relative",
        display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"center",
        textAlign:"center",padding:mob?"90px 20px 70px":"110px 40px 90px",
        background:`linear-gradient(150deg,${T.greenPale} 0%,${T.cream} 40%,#fff 100%)`,
        overflow:"hidden",
      }}>
        <div style={{position:"absolute",inset:0,pointerEvents:"none",opacity:0.033,backgroundImage:"radial-gradient(circle,#3a6b1f 1px,transparent 1px)",backgroundSize:"30px 30px"}}/>

        <Reveal>
          {/* ── BIG centered logo in hero */}
          <div style={{display:"flex",justifyContent:"center",marginBottom:22}}>
            <Logo size={mob?180:tab?260:340} variant="green" animated/>
          </div>

          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:mob?"44px":tab?"68px":"88px",fontWeight:900,color:T.text,margin:"0 0 8px",lineHeight:1,letterSpacing:"-0.01em"}}>
            𝖢 Λ 𝖱 𝖳 Ξ 𝖲 𝖨 Λ 𝖭   </h1>
          <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:mob?10:12,letterSpacing:"0.3em",color:T.greenMd,textTransform:"uppercase",fontWeight:700,margin:"0 0 22px"}}>
            Sheet Metal Fabrication · Power Press Stamping · Delhi NCR
          </p>
          <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:mob?15:18,color:T.textMd,maxWidth:560,lineHeight:1.88,margin:"0 auto 38px"}}>
            We operate <strong style={{color:T.green,fontWeight:700}}>heavy-duty power press machines</strong> to fabricate precision sheet metal components and custom parts — built to your exact drawings.
          </p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <Btn onClick={()=>setPage("Services")} v="primary">Our Services →</Btn>
            <Btn onClick={()=>setPage("Contact")} v="outline">Get a Quote</Btn>
          </div>
        </Reveal>

        <div style={{position:"absolute",bottom:26,left:"50%",transform:"translateX(-50%)",display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
          <div style={{width:1,height:40,background:`linear-gradient(${T.greenMd},transparent)`,animation:"spulse 2.2s ease infinite"}}/>
          <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:9,letterSpacing:"0.22em",color:T.textFt,textTransform:"uppercase"}}>scroll</span>
        </div>
      </section>

      {/* ── STATS */}
      <section style={{background:T.green,padding:mob?"36px 20px":"48px 40px"}}>
        <div style={{maxWidth:1000,margin:"0 auto",display:"grid",gridTemplateColumns:mob?"1fr 1fr":"repeat(4,1fr)",gap:mob?28:0}}>
          {STATS.map((s,i)=>(
            <Reveal key={i} delay={i*.08}>
              <div style={{textAlign:"center",padding:mob?0:"0 20px",borderRight:!mob&&i<3?"1px solid rgba(255,255,255,0.12)":"none"}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:mob?38:48,fontWeight:900,color:"#fff",lineHeight:1}}>{s.v}</div>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.13em",color:"rgba(255,255,255,.6)",textTransform:"uppercase",marginTop:5,fontWeight:700}}>{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── INTRO with your real photos */}
      <section style={{padding:mob?"64px 20px":"96px 40px",background:T.bgWhite}}>
        <div style={{maxWidth:1100,margin:"0 auto",display:"grid",gridTemplateColumns:tab?"1fr":"1fr 1fr",gap:tab?48:72,alignItems:"center"}}>
          <Reveal>
            <span style={EB()}>What We Do</span>
            <h2 style={H2S(mob?"28px":"clamp(26px,3vw,42px)")}>Sheet Metal Fabrication & Power Press Stamping.<br/>Built to Your Specs.</h2>
            <p style={PARA}>Cartesian Tools is a Delhi NCR–based sheet metal fabrication and power press stamping unit. We operate heavy-duty presses from 10T to 100T, delivering precision metal components and custom parts that fit and perform reliably.</p>
            <p style={{...PARA,marginTop:14}}>Whether you need a single prototype or a production run of thousands, we work from your drawings to deliver parts that are accurate, on-spec, and on time.</p>
            <div style={{display:"flex",gap:12,marginTop:28,flexWrap:"wrap"}}>
              <Btn onClick={()=>setPage("Services")} v="primary">See Services</Btn>
              <Btn onClick={()=>setPage("About")} v="ghost">About Us</Btn>
            </div>
          </Reveal>
          <Reveal delay={0.14}>
            {/* Real photos grid */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <AnimImg src={IMG_STAMP}       alt="Power press stamping" style={{gridColumn:"span 2",height:tab?190:220,borderRadius:10}}/>
              <AnimImg src={IMG_WORKER}      alt="Metal worker"         style={{height:135,borderRadius:8}} delay={0.1}/>
              <AnimImg src={IMG_FABRICATION} alt="Welding fabrication"  style={{height:135,borderRadius:8}} delay={0.2}/>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SERVICES CARDS */}
      <section style={{padding:mob?"64px 20px":"88px 40px",background:T.bg}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <Reveal>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:14,marginBottom:40}}>
              <div><span style={EB()}>What We Offer</span><h2 style={{...H2S(mob?"26px":"clamp(24px,2.8vw,40px)"),margin:0}}>Our Services</h2></div>
              {!mob&&<Btn onClick={()=>setPage("Services")} v="ghost" sx={{fontSize:11}}>View All →</Btn>}
            </div>
          </Reveal>
          <div style={{display:"grid",gridTemplateColumns:mob?"1fr":tab?"1fr 1fr":"repeat(4,1fr)",gap:16}}>
            {SERVICES.map((s,i)=>(
              <Reveal key={i} delay={i*.08}>
                <div style={{...CARD,overflow:"hidden",padding:0,transition:"transform .25s,box-shadow .25s",cursor:"default"}}
                  onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-5px)";e.currentTarget.style.boxShadow=`0 18px 44px ${T.shadowMd}`;}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow=`0 2px 18px ${T.shadow}`;}}>
                  {/* Animated image in card */}
                  <div style={{height:160,overflow:"hidden"}}>
                    <img src={s.img} alt={s.name} loading="lazy"
                      style={{width:"100%",height:"100%",objectFit:"cover",transition:"transform .5s"}}
                      onMouseEnter={e=>e.target.style.transform="scale(1.08)"}
                      onMouseLeave={e=>e.target.style.transform="scale(1)"}/>
                  </div>
                  <div style={{padding:"18px 16px"}}>
                    <div style={{fontSize:22,marginBottom:7}}>{s.icon}</div>
                    <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:17,color:T.text,margin:"0 0 5px",fontWeight:800}}>{s.name}</h3>
                    <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,color:T.textLt,lineHeight:1.6}}>{s.short}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          {mob&&<div style={{textAlign:"center",marginTop:22}}><Btn onClick={()=>setPage("Services")} v="ghost">View All Services →</Btn></div>}
        </div>
      </section>

      {/* ── MACHINES (with your real images) */}
      <section style={{padding:mob?"60px 20px":"80px 40px",background:T.bgWhite}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <Reveal>
            <span style={EB()}>Our Equipment</span>
            <h2 style={H2S(mob?"26px":"clamp(24px,2.8vw,40px)")}>Machines We Operate</h2>
          </Reveal>
          <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"repeat(3,1fr)",gap:16}}>
            {MACHINES.map((m,i)=>(
              <Reveal key={i} delay={i*.1}>
                <div style={{...CARD,padding:0,overflow:"hidden",borderLeft:`4px solid ${T.green}`}}>
                  <AnimImg src={m.img} alt={m.name} style={{height:160,borderRadius:0}} delay={i*.1}/>
                  <div style={{padding:"18px 16px"}}>
                    <div style={{fontSize:22,marginBottom:8}}>{m.icon}</div>
                    <div style={{fontFamily:"'Playfair Display',serif",fontSize:18,color:T.text,marginBottom:4,fontWeight:800}}>{m.name}</div>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.14em",color:T.greenMd,textTransform:"uppercase",marginBottom:8,fontWeight:700}}>{m.cap}</div>
                    <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:13,color:T.textLt}}>{m.use}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── PARTNERS */}
      <section style={{padding:mob?"48px 20px":"60px 40px",background:T.bg,borderTop:`1px solid ${T.border}`}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <Reveal><p style={{fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.28em",color:T.textFt,textTransform:"uppercase",textAlign:"center",marginBottom:22,fontWeight:700}}>Trusted By</p></Reveal>
          <div style={{display:"flex",flexWrap:"wrap",gap:10,justifyContent:"center"}}>
            {PARTNERS.map((p,i)=>(<div key={i} style={{fontFamily:"'DM Sans',sans-serif",fontSize:mob?12:13,fontWeight:600,padding:mob?"8px 14px":"10px 22px",border:`1.5px solid ${i===tick?T.green:T.border}`,borderRadius:50,background:i===tick?T.greenPale:T.bgWhite,color:i===tick?T.green:T.textLt,transition:"all .5s cubic-bezier(.4,0,.2,1)"}}>{p}</div>))}
          </div>
        </div>
      </section>

      {/* ── CTA — NO logo here, clean layout */}
      <section style={{padding:mob?"64px 20px":"96px 40px",background:`linear-gradient(135deg,${T.greenDk} 0%,${T.green} 55%,${T.greenMd} 100%)`,textAlign:"center",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",inset:0,opacity:0.04,backgroundImage:"radial-gradient(circle,#fff 1px,transparent 1px)",backgroundSize:"26px 26px"}}/>
        <Reveal>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:mob?"28px":"clamp(28px,4vw,52px)",color:"#fff",margin:"0 0 14px",fontWeight:900}}>Have a Part to Manufacture?</h2>
          <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:mob?14:17,color:"rgba(255,255,255,0.82)",maxWidth:480,margin:"0 auto 30px",lineHeight:1.85}}>Share your drawings — we'll respond with a quote within 24 hours.</p>
          <div style={{display:"flex",gap:16,justifyContent:"center",flexWrap:"wrap",marginBottom:28}}>
            <a href={`tel:${CO.phone1}`} style={{fontFamily:"'DM Sans',sans-serif",fontSize:15,color:"#fff",textDecoration:"none",fontWeight:700}}>📞 {CO.phone1}</a>
            <span style={{color:"rgba(255,255,255,.3)"}}>|</span>
            <a href={`tel:${CO.phone2}`} style={{fontFamily:"'DM Sans',sans-serif",fontSize:15,color:"#fff",textDecoration:"none",fontWeight:700}}>📞 {CO.phone2}</a>
          </div>
          <Btn onClick={()=>setPage("Contact")} v="white">Send Requirements →</Btn>
        </Reveal>
      </section>
    </div>
  );
}

// ── ABOUT ─────────────────────────────────────────────────────────────────
function AboutPage(){
  const vw=useVW(); const mob=vw<600; const tab=vw<1024;
  return(
    <div style={{paddingTop:mob?60:70,background:T.bg}}>
      <section style={{background:`linear-gradient(155deg,${T.greenPale} 0%,${T.bgWhite} 55%)`,padding:mob?"56px 20px 48px":"80px 40px 68px"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <Reveal>
            <span style={EB()}>Our Story</span>
            <h1 style={H1S(mob?"36px":"clamp(34px,5vw,60px)")}>About Cartesian Tools</h1>
            <p style={{...PARA,maxWidth:560}}>5+ years of precision sheet metal work and custom parts manufacturing in Delhi NCR.</p>
          </Reveal>
        </div>
      </section>
      <section style={{padding:mob?"52px 20px":"76px 40px",maxWidth:1100,margin:"0 auto"}}>
        <div style={{display:"grid",gridTemplateColumns:tab?"1fr":"1fr 1fr",gap:tab?44:64,alignItems:"start"}}>
          <Reveal>
            <AnimImg src={IMG_MACHINE} alt="Our facility" style={{height:tab?260:360,borderRadius:12,boxShadow:`0 8px 36px ${T.shadowMd}`}}/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:8}}>
              <AnimImg src={IMG_WORKER}  alt="Worker"  style={{height:120,borderRadius:8}} delay={0.1}/>
              <AnimImg src={IMG_TOOLING} alt="Tooling" style={{height:120,borderRadius:8}} delay={0.2}/>
            </div>
          </Reveal>
          <Reveal delay={0.14}>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:mob?22:28,color:T.text,margin:"0 0 16px",fontWeight:800}}>We Don't Just Press Metal —<br/>We Build Precision.</h2>
            <p style={PARA}>Cartesian Tools is a sheet metal fabrication and custom component manufacturing unit based in Delhi NCR. We operate power press machines from 10T to 100T and deliver components that fit precisely and perform reliably.</p>
            <p style={{...PARA,marginTop:12}}>Founded over 5 years ago, we've grown from a small workshop into a trusted manufacturing partner for companies across automotive, electrical, construction, and general engineering sectors.</p>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:28}}>
              {[["10T–100T","Press Capacity"],["5+","Years in Business"],["Pan-India","Delivery"],["24hr","Quote Turnaround"]].map(([v,l],i)=>(
                <div key={i} style={{background:T.bg,border:`1px solid ${T.border}`,borderRadius:10,padding:"16px 14px"}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:T.green,fontWeight:900}}>{v}</div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:10,color:T.textLt,marginTop:3,textTransform:"uppercase",letterSpacing:"0.08em",fontWeight:700}}>{l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
      <section style={{padding:mob?"44px 20px":"60px 40px",background:T.green}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <Reveal>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:mob?24:32,color:"#fff",margin:"0 0 22px",fontWeight:900}}>Clients & Industry Partners</h2>
            <div style={{display:"flex",flexWrap:"wrap",gap:10}}>
              {PARTNERS.map((p,i)=>(<span key={i} style={{fontFamily:"'DM Sans',sans-serif",fontSize:mob?12:13,fontWeight:600,color:"#fff",padding:"9px 18px",border:"1px solid rgba(255,255,255,0.28)",borderRadius:50}}>{p}</span>))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

// ── SERVICES ──────────────────────────────────────────────────────────────
function ServicesPage(){
  const vw=useVW(); const mob=vw<600; const tab=vw<1024;
  return(
    <div style={{paddingTop:mob?60:70,background:T.bg}}>
      <section style={{background:`linear-gradient(155deg,${T.greenPale} 0%,${T.bgWhite} 55%)`,padding:mob?"56px 20px 48px":"80px 40px 68px"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <Reveal>
            <span style={EB()}>What We Offer</span>
            <h1 style={H1S(mob?"36px":"clamp(34px,5vw,60px)")}>Our Services</h1>
            <p style={{...PARA,maxWidth:560}}>From raw sheet metal to finished custom parts — all in-house using power press equipment and skilled workforce.</p>
          </Reveal>
        </div>
      </section>
      <section style={{padding:mob?"52px 20px":"72px 40px",maxWidth:1100,margin:"0 auto"}}>
        <div style={{display:"grid",gap:24}}>
          {SERVICES.map((s,i)=>(
            <Reveal key={i} delay={i*.06}>
              <div style={{...CARD,display:"grid",gridTemplateColumns:tab?"1fr":i%2===0?"420px 1fr":"1fr 420px",gap:0,padding:0,overflow:"hidden"}}>
                {/* Animated real image */}
                <AnimImg src={s.img} alt={s.name}
                  style={{height:tab?220:"100%",minHeight:tab?0:280,borderRadius:0,order:!tab&&i%2!==0?2:0}}
                  delay={i*.07}/>
                <div style={{padding:mob?"24px 20px":"36px 32px",order:!tab&&i%2!==0?1:0}}>
                  <div style={{fontSize:28,marginBottom:10}}>{s.icon}</div>
                  <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:mob?22:28,color:T.text,margin:"0 0 10px",fontWeight:900}}>{s.name}</h2>
                  <p style={{...PARA,marginBottom:20}}>{s.desc}</p>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {s.feat.map((f,j)=>(<div key={j} style={{display:"flex",alignItems:"center",gap:8,fontFamily:"'DM Sans',sans-serif",fontSize:13,color:T.textMd}}><span style={{width:6,height:6,borderRadius:"50%",background:T.green,flexShrink:0}}/>{f}</div>))}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        {/* Machines in services */}
        <Reveal>
          <div style={{marginTop:40,background:T.green,borderRadius:14,padding:mob?"28px 20px":"40px 36px"}}>
            <span style={EB("rgba(255,255,255,0.5)")}>Our Equipment</span>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:mob?22:30,color:"#fff",margin:"0 0 24px",fontWeight:900}}>Machines We Use</h2>
            <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"repeat(3,1fr)",gap:14}}>
              {MACHINES.map((m,i)=>(<div key={i} style={{background:"rgba(255,255,255,0.1)",borderRadius:10,overflow:"hidden"}}>
                <div style={{height:140,overflow:"hidden"}}>
                  <img src={m.img} alt={m.name} loading="lazy" style={{width:"100%",height:"100%",objectFit:"cover",transition:"transform .4s"}}
                    onMouseEnter={e=>e.target.style.transform="scale(1.07)"}
                    onMouseLeave={e=>e.target.style.transform="scale(1)"}/>
                </div>
                <div style={{padding:"14px 14px"}}>
                  <div style={{fontSize:18,marginBottom:5}}>{m.icon}</div>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,color:"#fff",marginBottom:2,fontWeight:700}}>{m.name}</div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:9,color:"rgba(255,255,255,0.5)",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:4,fontWeight:600}}>{m.cap}</div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:12,color:"rgba(255,255,255,.82)"}}>{m.use}</div>
                </div>
              </div>))}
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

// ── QUALITY ───────────────────────────────────────────────────────────────
function QualityPage(){
  const vw=useVW(); const mob=vw<600;
  return(
    <div style={{paddingTop:mob?60:70,background:T.bg}}>
      <section style={{background:`linear-gradient(155deg,${T.greenPale} 0%,${T.bgWhite} 55%)`,padding:mob?"56px 20px 48px":"80px 40px 68px"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <Reveal>
            <span style={EB()}>Our Standards</span>
            <h1 style={H1S(mob?"36px":"clamp(34px,5vw,60px)")}>Quality at Every Stage</h1>
            <p style={{...PARA,maxWidth:560}}>Every component is inspected, measured, and verified before it leaves our facility.</p>
          </Reveal>
        </div>
      </section>
      <section style={{padding:mob?"52px 20px":"72px 40px",maxWidth:1100,margin:"0 auto"}}>
        <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr",gap:16,marginBottom:40}}>
          {QUALITY.map((q,i)=>(
            <Reveal key={i} delay={i*.08}>
              <div style={{...CARD,borderLeft:`4px solid ${T.green}`}}>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:T.green,marginBottom:4,fontWeight:900}}>{q.code}</div>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.2em",color:T.greenMd,textTransform:"uppercase",marginBottom:11,fontWeight:700}}>{q.lbl}</div>
                <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:14,color:T.textMd,lineHeight:1.8}}>{q.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div style={{background:T.green,borderRadius:14,padding:mob?"28px 18px":"40px 36px",marginBottom:32}}>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:mob?20:26,color:"#fff",margin:"0 0 28px",fontWeight:900}}>Production Quality Flow</h2>
            <div style={{display:"grid",gridTemplateColumns:mob?"repeat(2,1fr)":"repeat(6,1fr)",gap:14}}>
              {["Drawing\nReview","Material\nCheck","Press\nSetup","In-Process\nQC","Final\nInspect","Dispatch"].map((s,i)=>(
                <div key={i} style={{textAlign:"center"}}>
                  <div style={{width:42,height:42,borderRadius:"50%",border:"2px solid rgba(255,255,255,.4)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 10px",fontFamily:"'Playfair Display',serif",fontSize:17,color:"#fff",fontWeight:900}}>{i+1}</div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:11,color:"rgba(255,255,255,.82)",lineHeight:1.4,whiteSpace:"pre-line"}}>{s}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal>
          {/* Real tooling/die image at bottom of quality page */}
          <AnimImg src={IMG_TOOLING} alt="Quality tooling storage" style={{width:"100%",height:mob?200:340,borderRadius:12,boxShadow:`0 8px 36px ${T.shadowMd}`}}/>
        </Reveal>
      </section>
    </div>
  );
}

// ── CONTACT ───────────────────────────────────────────────────────────────
function ContactPage(){
  const [form,setForm]=useState({name:"",company:"",phone:"",email:"",service:"",message:""});
  const [sent,setSent]=useState(false);
  const [loading,setLoading]=useState(false);
  const [err,setErr]=useState("");
  const vw=useVW(); const mob=vw<600; const tab=vw<1024;
  const upd=e=>setForm(f=>({...f,[e.target.name]:e.target.value}));
  const submit=async()=>{
    if(!form.name||!form.phone){setErr("Name and Phone are required.");return;}
    setErr("");setLoading(true);
    // await addDoc(collection(db,"enquiries"),{...form,createdAt:serverTimestamp()});
    await addDoc(collection(db, 'enquiries'), {
  ...form,
  createdAt: serverTimestamp(),
  source: 'website',
})
    //await new Promise(r=>setTimeout(r,900));
    setSent(true);setLoading(false);
  };
  const inp={width:"100%",background:T.bg,border:`1.5px solid ${T.border}`,borderRadius:8,padding:"12px 14px",fontFamily:"'DM Sans',sans-serif",fontSize:14,color:T.text,boxSizing:"border-box",outline:"none",transition:"border-color .2s"};
  return(
    <div style={{paddingTop:mob?60:70,background:T.bg}}>
      <section style={{background:`linear-gradient(155deg,${T.greenPale} 0%,${T.bgWhite} 55%)`,padding:mob?"56px 20px 48px":"80px 40px 68px"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <Reveal>
            <span style={EB()}>Get In Touch</span>
            <h1 style={H1S(mob?"36px":"clamp(34px,5vw,60px)")}>Request a Quote</h1>
            <p style={{...PARA,maxWidth:500}}>Share your requirements — we respond within 24 hours with pricing and lead time.</p>
          </Reveal>
        </div>
      </section>
      <section style={{padding:mob?"48px 20px":"68px 40px",maxWidth:1100,margin:"0 auto"}}>
        <div style={{display:"grid",gridTemplateColumns:tab?"1fr":"1.1fr 0.9fr",gap:mob?36:52}}>
          <Reveal>
            {sent?(
              <div style={{...CARD,textAlign:"center",padding:mob?"40px 22px":"56px 40px"}}>
                <div style={{fontSize:54,marginBottom:14}}>✅</div>
                <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:26,color:T.text,margin:"0 0 10px",fontWeight:900}}>Enquiry Received!</h3>
                <p style={PARA}>Our team will contact you within 24 business hours with a quote.</p>
                <Btn onClick={()=>{setSent(false);setForm({name:"",company:"",phone:"",email:"",service:"",message:""});}} v="outline" sx={{marginTop:22}}>Send Another →</Btn>
              </div>
            ):(
              <div style={CARD}>
                <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:mob?22:24,color:T.text,margin:"0 0 22px",fontWeight:900}}>Send Your Requirements</h2>
                {err&&<div style={{background:"#fff5f5",border:"1.5px solid #fca5a5",borderRadius:7,padding:"11px 15px",marginBottom:16,fontFamily:"'DM Sans',sans-serif",fontSize:13,color:"#dc2626"}}>{err}</div>}
                <div style={{display:"grid",gridTemplateColumns:mob?"1fr":"1fr 1fr",gap:13,marginBottom:13}}>
                  {[["name","Full Name *","Your name"],["company","Company","Company name"],["phone","Phone *","+91 XXXXX XXXXX"],["email","Email","you@email.com"]].map(([n,l,ph])=>(
                    <div key={n}>
                      <label style={{fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.14em",color:T.textLt,textTransform:"uppercase",display:"block",marginBottom:5,fontWeight:700}}>{l}</label>
                      <input name={n} value={form[n]} onChange={upd} placeholder={ph} style={inp} onFocus={e=>e.target.style.borderColor=T.green} onBlur={e=>e.target.style.borderColor=T.border}/>
                    </div>
                  ))}
                </div>
                <div style={{marginBottom:13}}>
                  <label style={{fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.14em",color:T.textLt,textTransform:"uppercase",display:"block",marginBottom:5,fontWeight:700}}>Service Required</label>
                  <select name="service" value={form.service} onChange={upd} style={{...inp,appearance:"none"}} onFocus={e=>e.target.style.borderColor=T.green} onBlur={e=>e.target.style.borderColor=T.border}>
                    <option value="">Select a service...</option>
                    {SERVICES.map(s=><option key={s.name} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <div style={{marginBottom:20}}>
                  <label style={{fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.14em",color:T.textLt,textTransform:"uppercase",display:"block",marginBottom:5,fontWeight:700}}>Message</label>
                  <textarea name="message" value={form.message} onChange={upd} rows={4} placeholder="Material, thickness, quantity, tolerance..." style={{...inp,resize:"vertical"}} onFocus={e=>e.target.style.borderColor=T.green} onBlur={e=>e.target.style.borderColor=T.border}/>
                </div>
                <Btn onClick={submit} v="primary" full sx={{opacity:loading?.7:1,cursor:loading?"not-allowed":"pointer"}}>
                  {loading?"Sending...":"Send Enquiry →"}
                </Btn>
                <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:11,color:T.textFt,textAlign:"center",marginTop:11}}>We respond within 24 hours · No spam, guaranteed</p>
              </div>
            )}
          </Reveal>
          <Reveal delay={0.14}>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:mob?20:24,color:T.text,margin:"0 0 24px",fontWeight:900}}>Direct Contact</h2>
            {[["📞","Phone",[CO.phone1,CO.phone2],"tel:"],["✉","Email",[CO.email],"mailto:"],["📍","Location",[CO.addr],null],["🕐","Hours",[CO.hours],null]].map(([ic,l,vals,href],i,arr)=>(
              <div key={i} style={{display:"flex",gap:14,marginBottom:20,paddingBottom:20,borderBottom:i<arr.length-1?`1px solid ${T.border}`:"none"}}>
                <div style={{width:40,height:40,borderRadius:10,background:T.greenPale,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}>{ic}</div>
                <div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:9,letterSpacing:"0.18em",color:T.textFt,textTransform:"uppercase",marginBottom:4,fontWeight:700}}>{l}</div>
                  {vals.map((v,j)=>href
                    ?<a key={j} href={`${href}${v}`} style={{display:"block",fontFamily:"'DM Sans',sans-serif",fontSize:15,color:T.green,textDecoration:"none",fontWeight:700,marginBottom:2}}>{v}</a>
                    :<div key={j} style={{fontFamily:"'DM Sans',sans-serif",fontSize:14,color:T.textMd,marginBottom:2}}>{v}</div>
                  )}
                </div>
              </div>
            ))}
            <div style={{background:T.greenPale,border:`1.5px solid ${T.green}25`,borderRadius:12,padding:"18px 16px"}}>
              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:10,letterSpacing:"0.15em",color:T.green,textTransform:"uppercase",marginBottom:7,fontWeight:800}}>⚡ Quick Response</div>
              <p style={{fontFamily:"'DM Sans',sans-serif",fontSize:13,color:T.textMd,lineHeight:1.75}}>For urgent requirements, WhatsApp us at <strong style={{color:T.green}}>{CO.phone1}</strong> — we respond within 2 hours during business hours.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

// ── APP ROOT ──────────────────────────────────────────────────────────────
export default function App(){
  const [page,setPage]=useState("Home");
  useEffect(()=>{window.scrollTo({top:0,behavior:"smooth"});},[page]);
  const P={Home:<HomePage setPage={setPage}/>,About:<AboutPage/>,Services:<ServicesPage/>,Quality:<QualityPage/>,Contact:<ContactPage/>};
  return(
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800;900&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        html{scroll-behavior:smooth;}
        body{background:#f5f3ee;-webkit-font-smoothing:antialiased;}
        img{max-width:100%;display:block;}
        button{outline:none;}
        input,textarea,select{font-family:inherit;}
        *{-webkit-tap-highlight-color:transparent;}
        @keyframes logoFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
        @keyframes spulse{0%,100%{opacity:.25}50%{opacity:1}}
        button:active{transform:scale(.97)!important;}
      `}</style>
      <Navbar page={page} setPage={setPage}/>
      <main style={{minHeight:"100vh"}}>{P[page]}</main>
      <Footer setPage={setPage}/>
    </>
  );
}
